import {
  Component,
  OnInit,
  inject,
  ViewChild,
  OnDestroy,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  Inject,
  Optional,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  TranslateModule,
  TranslateService,
  LangChangeEvent,
} from '@ngx-translate/core';
import { UavPhotoService } from '@6grain/masofa-monolith-api-client/uav/api/uavPhoto.service';
import { UAVFlyPath } from '@6grain/masofa-monolith-api-client/uav/model/uAVFlyPath';
import { UAVFlyPathBaseGetQuery } from '@6grain/masofa-monolith-api-client/uav/model/uAVFlyPathBaseGetQuery';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { MsfTreeSelect } from '@components/tree-select/tree-select';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { FirmService } from '@6grain/masofa-monolith-api-client/dictionaries/api/firm.service';
import {
  Region,
  Crop,
  Firm,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import { UavUploadModalComponent } from './uav-upload-modal/uav-upload-modal.component';
import { UavEditModalComponent } from './uav-edit-modal/uav-edit-modal.component';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { buildFilterQuery } from '@app/utils/_msf';
import { SortType } from '@6grain/masofa-monolith-api-client/uav/model/sortType';
import { UavDataTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/uavDataType.service';
import { UavDataType } from '@6grain/masofa-monolith-api-client/dictionaries/model/uavDataType';
import { TagService } from '@6grain/masofa-monolith-api-client/dictionaries/api/tag.service';
import { Tag } from '@6grain/masofa-monolith-api-client/dictionaries';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { GenericSelectorComponent } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.component';
import { GenericSelectorConfig } from '@root/src/app/pages/Ui-Kit/generic-selector/generic-selector.models';
import { FilterOperator } from '../../utils/filter-operator';
import { withVisibleTagFilter } from '@app/utils/tag-filters';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/field.service';
import { Field } from '@6grain/masofa-monolith-api-client/crop-monitoring/model/field';
import { ServerTranslateService } from '@app/services/server-translate.service';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, takeUntil, finalize } from 'rxjs/operators';
import { OpenStreetMapComponent } from '@app/components/openstreetmap/openstreetmap.component';
import {
  MapControllerInput,
  MapInitOptions,
  Marker,
} from '@app/components/openstreetmap/interfaces';
import { ConfirmationModalComponent } from '../Ui-Kit/confirmation-modal/confirmation-modal.component';
import { environment } from '@root/src/environments/environment';
import { BackButton } from '@components/ui/back-button/back-button';
import { UavGalleryModalComponent } from './uav-gallery-modal/uav-gallery-modal.component';
import { RegionDisplay } from '@components/region-display/region-display';
import { UavRepresentativePhotoDto } from '@6grain/masofa-monolith-api-client/uav/model/uavRepresentativePhotoDto';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {MsfInput} from '@components/ui/forms/input/input';
import {MsfSelect} from '@components/select/select';
import {UavSourceTypeLabelPipe} from '@app/utils/pipes/uav-source-type-label-pipe';

declare var L: any;

interface ExtendedUAVFlyPath extends UAVFlyPath {
  representativePhoto?: UavRepresentativePhotoDto;
  tagNames?: string;
  sourceType?: string;
}

@Component({
  selector: 'app-uav-data',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    UavUploadModalComponent,
    UavEditModalComponent,
    ServerTranslatePipe,
    MsfTreeSelect,
    OpenStreetMapComponent,
    DatePickerComponent,
    GenericSelectorComponent,
    ConfirmationModalComponent,
    BackButton,
    UavGalleryModalComponent,
    RegionDisplay,
    PrimaryButton,
    SecondaryButton,
    MsfInput,
    MsfSelect,
    UavSourceTypeLabelPipe,
  ],
  providers: [DatePipe, DateFormatLangPipe],
  templateUrl: './uav-data.component.html',
  styleUrls: ['./uav-data.component.scss'],
})
export class UavDataComponent implements OnInit, OnDestroy {
  private readonly uavPhotoService = inject(UavPhotoService);
  public readonly regionManagerService = inject(RegionManagerService);
  private readonly cropService = inject(CropService);
  private readonly firmService = inject(FirmService);
  public readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  private readonly uavDataTypeService = inject(UavDataTypeService);
  private readonly tagService = inject(TagService);
  private readonly fieldService = inject(FieldService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @ViewChild(OpenStreetMapComponent) osmComponent!: OpenStreetMapComponent;
  @ViewChildren(GenericSelectorComponent) selectors!: QueryList<
    GenericSelectorComponent<any>
  >;

  surveys: ExtendedUAVFlyPath[] = [];
  regions: Region[] = [];
  uavDataTypes: UavDataType[] = [];
  totalCount: number = 0;
  isLoading: boolean = false;
  /**
   * The last load failed, so the page has no answer for the current filters.
   * Distinguishes "nothing matched" from "we could not find out", which otherwise look identical.
   */
  loadFailed: boolean = false;
  isUploadModalVisible: boolean = false;
  isEditModalVisible: boolean = false;
  isDeleteConfirmVisible: boolean = false;
  isGalleryModalVisible: boolean = false;

  selectedGallerySurvey: UAVFlyPath | null = null;
  surveyToDelete: UAVFlyPath | null = null;
  selectedSurvey: UAVFlyPath | null = null;
  selectedSurveyId: string | null = null;

  private currentOsmRouteLayer: any = null;
  private currentFieldsLayer: any = null;
  private regionsLoaded = false;

  defaultSortBy: string = 'CreateAt';
  defaultSortOrder: SortType = SortType.NUMBER_1;

  pageSize: number = 4;
  currentPage: number = 1;
  totalPages: number = 1;
  filters: any = {
    name: null,
    regionId: null,
    cropId: null,
    firmId: null,
    startDate: null,
    endDate: null,
    dataType: null,
    tagId: null,
    tagIds: [],
    fieldId: null,
    sourceType: null,
  };

  sourceTypeOptions: { id: string; label: string }[] = [
    { id: 'Manual', label: 'UAV_SOURCE_MANUAL' },
    { id: 'Ftp', label: 'UAV_SOURCE_FTP' },
    { id: 'Api', label: 'UAV_SOURCE_API' },
  ];

  osmMapController = new Subject<MapControllerInput>();
  osmMapOptions: MapInitOptions = {
    center: [environment.map.lat, environment.map.lng],
    zoom: 6,
    height: '100%',
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: true,
    baseMap: 'osm',
    showRegionNames: true,
    disableBounds: true,
  };

  firmSelectorConfig: GenericSelectorConfig<Firm> = {
    searchField: 'ShortName',
    displayField: 'shortName',
    idField: 'id',
    getItems: (params) =>
      this.firmService.dictionariesFirmGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: params.filters,
        sortBy: 'ShortName',
        sort: SortType.NUMBER_0,
      }),
    getTotalCount: (params) =>
      this.firmService.dictionariesFirmGetTotalCountPost({
        filters: params.filters,
      }),
  };

  tagSelectorConfig: GenericSelectorConfig<Tag> = {
    searchField: 'Name',
    displayField: 'name',
    idField: 'id',
    getItems: (params) =>
      this.tagService.dictionariesTagGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: withVisibleTagFilter(params.filters),
        sortBy: 'Name',
        sort: SortType.NUMBER_0,
      }),
    getTotalCount: (params) =>
      this.tagService.dictionariesTagGetTotalCountPost({
        filters: withVisibleTagFilter(params.filters),
      }),
  };

  cropSelectorConfig: GenericSelectorConfig<Crop> = {
    searchField: 'Names',
    displayField: 'names',
    idField: 'id',
    formatLabel: (item) =>
      (this.serverTranslate.translate(item, 'names') as string) || '',
    getItems: (params) =>
      this.cropService
        .dictionariesCropGetByQueryPost({
          offset: params.offset,
          take: params.take,
          filters: params.filters,
        })
        .pipe(
          map((items) => {
            return items.sort((a, b) => {
              const nameA =
                (this.serverTranslate.translate(a, 'names') as string) || '';
              const nameB =
                (this.serverTranslate.translate(b, 'names') as string) || '';
              return nameA.localeCompare(nameB);
            });
          })
        ),
    getTotalCount: (params) =>
      this.cropService.dictionariesCropGetTotalCountPost({
        filters: params.filters,
      }),
  };

  fieldSelectorConfig: GenericSelectorConfig<Field> = {
    searchField: 'Name',
    displayField: 'name',
    idField: 'id',
    getItems: (params) =>
      this.fieldService.cropMonitoringFieldGetByQueryPost({
        offset: params.offset,
        take: params.take,
        filters: params.filters,
        sortBy: 'Name',
        sort: SortType.NUMBER_0,
      }),
    getTotalCount: (params) =>
      this.fieldService.cropMonitoringFieldGetTotalCountPost({
        filters: params.filters,
      }),
  };

  constructor() {}

  onMapReady(): void {
    this.loadRegionsOnce();
    setTimeout(() => {
      this.osmMapController.next({invalidateSize: true});
    }, 200);
  }

  private loadRegionsOnce(): void {
    if (this.regionsLoaded) {
      return;
    }
    const osm = this.osmComponent;
    if (!osm) {
      return;
    }
    this.regionManagerService
      .toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (polygons) => {
          this.regionsLoaded = true;
          if (polygons?.length) {
            this.regionManagerService.configurePolygonEvents(
              polygons,
              this.osmMapOptions,
              undefined,
            );
            this.osmMapController.next({addGisPolygons: polygons});
            setTimeout(() => {
              this.osmMapController.next({
                setShowRegionNames: this.osmMapOptions.showRegionNames,
              });
            }, 100);
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('UAV data: failed to load region polygons', err);
          this.cdr.markForCheck();
        },
      });
  }

  ngOnInit(): void {
    this.loadDictionaries();
    this.loadData();

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {
        if (this.selectors) {
          this.selectors.forEach((selector) => {
            selector.loadData(true);
          });
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.clearRoute();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDictionaries(): void {
    this.uavDataTypeService
      .dictionariesUavDataTypeGetByQueryPost({})
      .subscribe((res) => {
        this.uavDataTypes = res || [];
      });
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    const offset = (this.currentPage - 1) * this.pageSize;
    const queryFilters: any = {};
    if (this.filters.regionId) queryFilters['regionId'] = this.filters.regionId;
    if (this.filters.cropId) queryFilters['CropId'] = this.filters.cropId;
    if (this.filters.firmId) queryFilters['FirmId'] = this.filters.firmId;
    if (this.filters.fieldId) queryFilters['FieldId'] = this.filters.fieldId;
    if (this.filters.dataType)
      queryFilters['DataTypeId'] = this.filters.dataType;
    if (this.filters.sourceType)
      queryFilters['SourceType'] = this.filters.sourceType;
    const searchFilters: any[] = buildFilterQuery(queryFilters);
    if (this.filters.name) {
      searchFilters.push({
        filterField: 'Name',
        filterOperator: FilterOperator.Contains,
        filterValue: this.filters.name,
      });
    }
    await this.regionManagerService.includeChildInRequest(
      { regionId: this.filters.regionId },
      searchFilters as any,
    );
    if (this.filters.tagId) {
      searchFilters.push({
        filterField: 'TagIds',
        filterOperator: FilterOperator.Equals,
        filterValue: this.filters.tagId,
      });
    } else if (this.filters.tagIds && this.filters.tagIds.length > 0) {
      searchFilters.push({
        filterField: 'TagIds',
        filterOperator: FilterOperator.Equals,
        filterValue: this.filters.tagIds,
      });
    }
    if (this.filters.startDate) {
      const dateValue = new Date(this.filters.startDate);
      searchFilters.push({
        filterField: 'OriginalDate',
        filterOperator: FilterOperator.GreaterThanOrEqual,
        filterValue: this.datePipe.transform(dateValue, 'MM.dd.yyyy'),
      });
    }
    if (this.filters.endDate) {
      const dateValue = new Date(this.filters.endDate);
      searchFilters.push({
        filterField: 'OriginalDate',
        filterOperator: FilterOperator.LessThanOrEqual,
        filterValue: this.datePipe.transform(dateValue, 'MM.dd.yyyy'),
      });
    }

    const query: UAVFlyPathBaseGetQuery = {
      offset: offset,
      take: this.pageSize,
      filters: searchFilters,
      sortBy: this.defaultSortBy,
      sort: this.defaultSortOrder,
    };

    this.loadFailed = false;

    this.uavPhotoService.uavUavPhotoGetByQueryPost(query).subscribe({
      next: (surveys) => {
        this.isLoading = false;
        this.surveys = (surveys as ExtendedUAVFlyPath[]) || [];
        this.selectedSurveyId = null;
        this.clearFieldsLayer();
        this.clearRoute();
        this.osmMapController.next({ clearMarkers: true });
      },
      error: (err) => {
        console.error('Error loading UAV surveys:', err);
        this.isLoading = false;
        // Keeping the previous page would present the results of the *previous* filters as the
        // answer to the current ones - the same "the filter did nothing" symptom this fix targets.
        this.clearResults();
      },
    });

    this.uavPhotoService
      .uavUavPhotoGetTotalCountPost({ filters: searchFilters })
      .subscribe({
        next: (count) => {
          this.totalCount = count;
          this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
        },
        error: (err) => {
          console.error('Error getting count:', err);
          // A stale count sizes the pager for a result set that is no longer on screen.
          this.clearResults();
        },
      });
  }

  /**
   * Drops the result of a request that failed, so nothing stale is shown as the current answer.
   */
  private clearResults(): void {
    this.loadFailed = true;
    this.surveys = [];
    this.totalCount = 0;
    this.totalPages = 1;
    this.selectedSurveyId = null;
    this.clearFieldsLayer();
    this.clearRoute();
    this.osmMapController.next({ clearMarkers: true });
  }

  onClickIndex(index: String): void{
     if (this.selectedSurveyId) {
      this.osmMapController.next({
          removeCustomLayer: { id: this.selectedSurveyId! }
      })

      this.osmMapController.next({
        addCustomLayer: {
          id: this.selectedSurveyId!,
          getUrl: (coords) => {
            const baseUrl = `${environment.api}/tiles/Map/GetTile`;
            const zoom = coords.z;
            const x = coords.x;
            const y = coords.y;
            const n = Math.PI - 2 * Math.PI * (y + 0.5) / Math.pow(2, zoom);
            const lat = (180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))));
            const lon = (x + 0.5) / Math.pow(2, zoom) * 360 - 180;
            return `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=uz_uav:uav_ndvi_${this.selectedSurveyId}&coordinateSystem=0`
          }
        }
      })
    }
  }

  onCardClick(survey: UAVFlyPath): void {
    if (this.selectedSurveyId) {
      this.osmMapController.next({
          removeCustomLayer: { id: this.selectedSurveyId! }
      })
    }
    this.selectedSurveyId = survey.id || null;
    this.clearRoute();
    this.clearFieldsLayer();
    this.osmMapController.next({ clearMarkers: true });
    this.osmMapController.next({
      addCustomLayer: {
        id: this.selectedSurveyId!,
        getUrl: (coords) => {
          const baseUrl = `${environment.api}/tiles/Map/GetTile`;
          const zoom = coords.z;
          const x = coords.x;
          const y = coords.y;
          const n = Math.PI - 2 * Math.PI * (y + 0.5) / Math.pow(2, zoom);
          const lat = (180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))));
          const lon = (x + 0.5) / Math.pow(2, zoom) * 360 - 180;
          return `${baseUrl}?lat=${lat}&lon=${lon}&z=${zoom}&layer=uz_uav:uav_orto_${this.selectedSurveyId}&coordinateSystem=0`
        }
      }
    })
    const mapInstance = this.osmComponent?.getMapInstance();
    if (!mapInstance || typeof L === 'undefined') return;
    const totalBounds = L.latLngBounds([]);
    const osmMarkers: Marker[] = [];
    const collections =
      (survey as any).uavPhotoCollections || (survey as any).collections || [];
    if (collections.length > 0) {
      collections.forEach((collection: any) => {
        const coords = this.extractCoordinates(collection.pointJson);
        if (coords) {
          const photos = collection.uavPhotos || collection.photos || [];
          const mainPhoto = photos.length > 0 ? photos[0] : null;
          const previewId = collection.previewFileStorageId;
          const popupContent = this.buildMarkerPopup(
            collection,
            mainPhoto,
            previewId
          );
          const iconHtml = this.getCustomMarkerIcon();
          osmMarkers.push({
            id: collection.id,
            lat: coords.lat,
            lng: coords.lng,
            title: mainPhoto ? mainPhoto.title : this.translate.instant('Survey point'),
            popup: popupContent,
            customIcon: iconHtml,
            onClick: () => {},
          });
          totalBounds.extend([coords.lat, coords.lng]);
        }
      });
    }
    let pathCoords: [number, number][] = [];
    if (survey.flyPathJson) {
      pathCoords = this.extractPathCoordinates(survey.flyPathJson);
      if (pathCoords && pathCoords.length > 0) {
        this.currentOsmRouteLayer = L.polyline(pathCoords, {
          color: '#FF5722',
          weight: 4,
          opacity: 0.8,
          dashArray: '10, 5',
        });
        this.currentOsmRouteLayer.addTo(mapInstance);
        totalBounds.extend(this.currentOsmRouteLayer.getBounds());
      }
    }
    if (osmMarkers.length > 0) {
      this.osmMapController.next({ addMarkers: osmMarkers });
    }
    const fieldsObservable = survey.id
      ? this.uavPhotoService.uavUavPhotoGetFieldRelationsSurveyIdGet(survey.id)
      : of([]);

    fieldsObservable.subscribe({
      next: (relations) => {
        if (relations && relations.length > 0) {
          const fieldsLayer = this.drawFieldContours(relations);
          if (fieldsLayer) {
            totalBounds.extend(fieldsLayer.getBounds());
          }
        }
        if (totalBounds.isValid()) {
          mapInstance.fitBounds(totalBounds, { padding: [50, 50] });
        }
      },
      error: (err) => {
        console.error('Error loading fields', err);
        if (totalBounds.isValid()) {
          mapInstance.fitBounds(totalBounds, { padding: [50, 50] });
        }
      },
    });
  }

  private clearFieldsLayer(): void {
    if (this.currentFieldsLayer && this.osmComponent) {
      const map = this.osmComponent.getMapInstance();
      if (map) {
        map.removeLayer(this.currentFieldsLayer);
      }
    }
    this.currentFieldsLayer = null;
  }

  private drawFieldContours(relations: any[]): any {
    if (typeof L === 'undefined' || !this.osmComponent) return null;
    const polygons: any[] = [];
    relations.forEach((rel) => {
      if (rel.fieldPolygonWkt) {
        const coords = this.extractPolygonCoordinates(rel.fieldPolygonWkt);
        if (coords.length > 0) {
          const polygon = L.polygon(coords, {
            color: '#FFD700',
            weight: 2,
            fillOpacity: 0.2,
          });
          polygons.push(polygon);
        }
      }
    });

    if (polygons.length > 0) {
      const map = this.osmComponent.getMapInstance();
      if (map) {
        this.currentFieldsLayer = L.featureGroup(polygons);
        this.currentFieldsLayer.addTo(map);
        return this.currentFieldsLayer;
      }
    }
    return null;
  }

  private extractPolygonCoordinates(wkt: string): [number, number][] {
    try {
      const content = wkt.replace(/^POLYGON\s*\(\(/i, '').replace(/\)\)$/, '');
      const pairs = content.split(',');
      return pairs.map((pair) => {
        const trimmed = pair.trim();
        const [lng, lat] = trimmed.split(/\s+/).map(Number);
        return [lat, lng] as [number, number];
      });
    } catch (e) {
      console.warn('Error parsing polygon WKT', e);
      return [];
    }
  }

  private clearRoute(): void {
    if (this.currentOsmRouteLayer && this.osmComponent) {
      const osmInstance = this.osmComponent.getMapInstance();
      if (osmInstance) {
        osmInstance.removeLayer(this.currentOsmRouteLayer);
      }
    }
    this.currentOsmRouteLayer = null;
  }

  private extractCoordinates(
    pointJson: string | null | undefined
  ): { lat: number; lng: number } | null {
    if (!pointJson) return null;
    let lat: number | null = null;
    let lng: number | null = null;

    if (pointJson.trim().startsWith('{')) {
      try {
        const geoObj = JSON.parse(pointJson);
        if (geoObj.coordinates && Array.isArray(geoObj.coordinates)) {
          lng = geoObj.coordinates[0];
          lat = geoObj.coordinates[1];
        }
      } catch (e) {
        console.warn('JSON parse error', e);
      }
    } else {
      try {
        const match = pointJson.match(/\(([^)]+)\)/);
        if (match && match[1]) {
          const parts = match[1].trim().split(/\s+/).map(Number);
          if (parts.length >= 2) {
            lng = parts[0];
            lat = parts[1];
          }
        }
      } catch (e) {
        console.warn('WKT parse error', e);
      }
    }

    if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
    return null;
  }

  private extractPathCoordinates(wkt: string): [number, number][] {
    if (!wkt) return [];
    try {
      const content = wkt.replace(/^LINESTRING\s*\(/i, '').replace(/\)$/, '');
      const pairs = content.split(',');
      const coords: [number, number][] = pairs
        .map((pair) => {
          const trimmed = pair.trim();
          if (!trimmed) return null;
          const [lngStr, latStr] = trimmed.split(/\s+/);
          const lat = parseFloat(latStr);
          const lng = parseFloat(lngStr);
          if (!isNaN(lat) && !isNaN(lng)) {
            return [lat, lng];
          }
          return null;
        })
        .filter((p) => p !== null) as [number, number][];

      return coords;
    } catch (e) {
      console.warn('Error parsing flyPathJson:', e);
      return [];
    }
  }

  getImageUrl(photoId: string | null | undefined): string {
  if (!photoId) {
    return 'assets/images/uav-placeholder.png';
  }
  return `${environment.api}/uav/UavPhoto/GetImage/${photoId}`;
}

  private buildMarkerPopup(
    collection: any,
    photo: any,
    previewId?: string
  ): string {
    const dateStr =
      this.datePipe.transform(
        photo?.originalDate || collection?.createAt,
        'dd.MM.yyyy'
      ) || '-';

    const title = photo?.title || this.translate.instant('Photo');
    const imageUrl = this.getImageUrl(previewId);
    return `
       <div style="text-align:center; min-width: 160px;">
         <div style="font-weight:600; margin-bottom:4px; word-wrap: break-word; max-width: 200px;">
            ${title}
         </div>
         <div style="font-size:12px; color:#666; margin-bottom:8px;">${dateStr}</div>
         <div style="width:100%; height:100px; background:#f0f0f0; border-radius:4px; overflow:hidden;">
           <img src="${imageUrl}"
                style="width:100%; height:100%; object-fit:cover;"
                onerror="this.src='assets/images/uav-placeholder.png'">
         </div>
       </div>
     `;
  }

  private getCustomMarkerIcon(): string {
    return `
      <div style="width: 20px; height: 20px; display: flex; align-items: center; justify-content: center;">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));">
          <circle cx="12" cy="12" r="9" fill="#FF5722" stroke="white" stroke-width="3"/>
        </svg>
      </div>
    `;
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadData();
  }

  resetFilters(): void {
    this.filters = {
      name: null,
      regionId: null,
      cropId: null,
      firmId: null,
      startDate: null,
      endDate: null,
      dataType: null,
      tagId: null,
      tagIds: [],
      fieldId: null,
      sourceType: null,
    };
    this.applyFilters();
  }

  onDeleteSurvey(survey: UAVFlyPath): void {
    if (!survey.id) return;
    this.surveyToDelete = survey;
    this.isDeleteConfirmVisible = true;
  }

  onConfirmDelete(): void {
    if (!this.surveyToDelete || !this.surveyToDelete.id) {
      this.closeDeleteConfirm();
      return;
    }

    this.isLoading = true;
    this.uavPhotoService
      .uavUavPhotoDeleteSurveyIdDelete(this.surveyToDelete.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.closeDeleteConfirm();
        })
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.loadData();
          }
        },
        error: (err) => {
          console.error('Error deleting:', err);
        },
      });
  }
  closeDeleteConfirm(): void {
    this.isDeleteConfirmVisible = false;
    this.surveyToDelete = null;
  }

  openUploadModal(): void {
    this.isUploadModalVisible = true;
  }

  closeUploadModal(): void {
    this.isUploadModalVisible = false;
  }

  openGalleryModal(survey: UAVFlyPath): void {
    this.selectedGallerySurvey = survey;
    this.isGalleryModalVisible = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalVisible = false;
    this.selectedGallerySurvey = null;
  }

  onUploadSuccess(): void {
    this.closeUploadModal();
    this.loadData();
  }

  onUpdateSuccess(): void {
    this.loadData();
  }

  openEditModal(survey: UAVFlyPath): void {
    this.selectedSurvey = survey;
    this.isEditModalVisible = true;
  }

  closeEditModal(): void {
    this.isEditModalVisible = false;
    this.selectedSurvey = null;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadData();
    }
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = 'assets/images/uav-placeholder.png';
  }

  onRegionSelected(regionId: string | null): void {
    this.filters.regionId = regionId;
  }

  getRegionDisplayInfo(regionId: string | null | undefined): {
    region: Region | null;
    district: Region | null;
  } {
    if (!regionId || !this.regions.length) {
      return { region: null, district: null };
    }
    const currentRegion = this.regions.find((r) => r.id === regionId);
    if (!currentRegion) {
      return { region: null, district: null };
    }
    if (currentRegion.parentId && currentRegion.parentId !== currentRegion.id) {
      const parentRegion = this.regions.find(
        (r) => r.id === currentRegion.parentId
      );
      if (parentRegion) {
        return { region: parentRegion, district: currentRegion };
      }
    }
    return { region: currentRegion, district: null };
  }

  get visiblePages(): (number | string)[] {
    if (this.totalPages <= 1) return [1];
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 1;
    const range: number[] = [];
    const rangeWithDots: (number | string)[] = [];
    let l: number | undefined;
    range.push(1);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i < total && i > 1) {
        range.push(i);
      }
    }
    range.push(total);
    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  }
}
